import React, { useEffect, useRef } from 'react';
import { PrecisionManufacturing as PrecisionManufacturingIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface ChangeOrderManufacturingCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const ManufacturingCardFields = [
  { name: 'processValidationImpact', label: 'Impact to PVAL/PVER?' },
  { name: 'pfmeaImpact', label: 'Impact to PFMEA?' },
  { name: 'procedureImpact', label: 'Affects procedures?' },
  { name: 'safetyImpact', label: 'Impact to Safety i.e. PPE?' },
  { name: 'sdsNeeded', label: 'New SDS needed?' },
  { name: 'equipmentImpact', label: 'Impact to equipment/fixtures/gages?' },
  {
    name: 'calibrationImpact',
    label: 'Impact to PM/CAL?'
  },
  {
    name: 'manufacturingRegulatoryImpact',
    label:
      'Does modification affect devices, components, labeling, or software that have received regulatory (FDA, EU NB, etc.) clearance/approval or are currently under review? If yes, complete Regulatory Impact Analysis'
  },
  {
    name: 'clinicalTrialImpact',
    label:
      'Does modification affect devices, components, labeling, or software currently being studied in a clinical trial? If yes, complete Clinical Impact Analysis'
  }
];

export const ChangeOrderManufacturingCard: React.FC<
  ChangeOrderManufacturingCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    processValidationImpact: changeOrder.processValidationImpact || '',
    pfmeaImpact: changeOrder.pfmeaImpact || '',
    procedureImpact: changeOrder.procedureImpact || '',
    safetyImpact: changeOrder.safetyImpact || '',
    sdsNeeded: changeOrder.sdsNeeded || '',
    equipmentImpact: changeOrder.equipmentImpact || '',
    calibrationImpact: changeOrder.calibrationImpact || '',
    manufacturingRegulatoryImpact:
      changeOrder.manufacturingRegulatoryImpact || '',
    clinicalTrialImpact: changeOrder.clinicalTrialImpact || '',
    commentsManufacturing: changeOrder.commentsManufacturing || '',
    manufacturingDueDate: changeOrder.manufacturingDueDate,
    manufacturingCompletedDate: changeOrder.manufacturingCompletedDate
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);
  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <PrecisionManufacturingIcon />
          Manufacturing Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {ManufacturingCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                disabled={isFieldDisabled(field.name)}
                label={field.label}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='manufacturingDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('manufacturingDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='manufacturingCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('manufacturingCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsManufacturing'
              label='Comments'
              disabled={isFieldDisabled('commentsManufacturing')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
